import { useState } from 'react'
import { useMutation } from '~/shared/hooks/useMutation'
import AddIcon from '@mui/icons-material/Add'
import ClearIcon from '@mui/icons-material/Clear'
import { patientsSlice } from '~/entities/person'

import { Autocomplete, Box, Button, Grid, IconButton, TextField, Typography } from '@mui/material'
import type { PatientUser } from '~/shared/types'
import { useAppDispatch } from '~/app/store/hooks'

interface EditMedicalEquipmentProps {
  patient: PatientUser
}

interface EquipmentItem {
  name: string
  type: string
}

const newEquipmentItem = (): EquipmentItem => {
  return {
    name: '',
    type: '',
  }
}

const TYPE_CHOICES: Record<string, string | undefined> = {
  bp_cuff_or_monitor: 'BP Cuff/Monitor',
  glucometer: 'Glucometer',
  weight_scale: 'Weight Scale',
  spirometer: 'Spirometer',
  fertility_tracker: 'Fertility Tracker',
  sleep_study: 'Sleep Study',
  activity_tracker: 'Activity Tracker',
  ekg: 'EKG',
  ekg_and_stethoscope: 'EKG & Stethoscope',
  pulse_oximeter: 'Pulse Oximeter',
  thermometer: 'Thermometer',
  peak_flow_meter: 'Peak Flow Meter',
  other: 'Other',
}

const NAME_CHOICES: Record<string, string | undefined> = {
  ihealth: 'iHealth',
  other: 'Other',
}

const BP_CUFF_TYPES: Record<string, string | undefined> = {
  cellular: 'Cellular',
  nonCellular: 'Non-Cellular',
  selfSupply: 'Self Supply',
  ihealth: 'iHealth',
  other: 'Other',
}

const equipmentItemsArrayToObject = (
  equipmentArray: EquipmentItem[],
): Record<string, EquipmentItem> => {
  const newEquipment: Record<string, EquipmentItem> = {}
  equipmentArray.forEach((equipmentItem, i) => {
    newEquipment[i] = equipmentItem
  })

  return newEquipment
}

interface EditableEquipmentData {
  [key: string]: EquipmentItem | null
}

const isSaveable = (obj: EquipmentItem | null): boolean => obj != null && !!obj.name && !!obj.type

const EditMedicalEquipment = (props: EditMedicalEquipmentProps) => {
  const [equipmentData, setEquipmentData] = useState<EditableEquipmentData>({})
  const dispatch = useAppDispatch()

  const { run: submit, loading: loadingSubmit } = useMutation(
    async (equipmentData: EditableEquipmentData) => {
      const medical_equipment_data = Object.values(equipmentData).filter(isSaveable)
      await dispatch(
        patientsSlice.thunks.updatePatient({
          id: props.patient.id,
          person: { medical_equipment_data },
        }),
      )
    },
  )

  // The editable rows reset to the patient's saved equipment whenever that changes.
  const savedEquipment = props.patient.person.medical_equipment_data
  const [loadedEquipment, setLoadedEquipment] = useState<typeof savedEquipment>()
  if (savedEquipment !== loadedEquipment) {
    setLoadedEquipment(savedEquipment)
    // `medical_equipment_data` is untyped JSON on the wire (`z.unknown()` in the
    // generated client), so the shape is only asserted here, where it is read.
    const medicalEquipmentData = savedEquipment as EquipmentItem[] | null
    setEquipmentData(medicalEquipmentData ? equipmentItemsArrayToObject(medicalEquipmentData) : {})
  }

  const handleNameChange = (itemId: string, value: string) => {
    const currentItem = equipmentData[itemId]
    if (!currentItem) return
    setEquipmentData({ ...equipmentData, [itemId]: { ...currentItem, name: value } })
  }

  const handleTypeChange = (itemId: string, value: string) => {
    const currentItem = equipmentData[itemId]
    if (!currentItem) return
    setEquipmentData({ ...equipmentData, [itemId]: { ...currentItem, type: value } })
  }

  const addNewItem = () => {
    const keys = Object.keys(equipmentData)
    const lastKey = keys[keys.length - 1]
    let newId = 0
    if (lastKey != null) {
      newId = parseInt(lastKey) + 1
    }
    setEquipmentData({ ...equipmentData, [newId]: newEquipmentItem() })
  }

  const removeItem = (itemId: string) => {
    setEquipmentData({ ...equipmentData, [itemId]: null })
  }

  return (
    <>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6">Medical Equipment</Typography>
      </Box>
      <Box sx={{ mt: 1 }}>
        {Object.keys(equipmentData).length === 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography>No equipment added yet</Typography>
          </Box>
        )}
        {Object.keys(equipmentData).map(key => {
          if (equipmentData[key] == null) return null
          return (
            <Box key={key} sx={{ mb: 1 }}>
              <Grid container spacing={2}>
                <Grid container spacing={2} size={11}>
                  <Grid size={6}>
                    <Autocomplete
                      autoHighlight
                      value={equipmentData[key].type}
                      onChange={(_, value) => handleTypeChange(key, value ?? '')}
                      options={Object.keys(TYPE_CHOICES)}
                      getOptionLabel={option => TYPE_CHOICES[option] || ''}
                      renderInput={params => (
                        <TextField {...params} label="Type" variant="outlined" />
                      )}
                      style={{ width: '100%' }}
                    />
                  </Grid>
                  <Grid size={6}>
                    <Autocomplete
                      autoHighlight
                      value={equipmentData[key].name}
                      onChange={(_, value) => handleNameChange(key, value ?? '')}
                      options={
                        equipmentData[key].type === 'bp_cuff_or_monitor'
                          ? Object.keys(BP_CUFF_TYPES)
                          : Object.keys(NAME_CHOICES)
                      }
                      getOptionLabel={
                        equipmentData[key].type === 'bp_cuff_or_monitor'
                          ? option => BP_CUFF_TYPES[option] || ''
                          : option => NAME_CHOICES[option] || ''
                      }
                      renderInput={params => (
                        <TextField {...params} label="Name" variant="outlined" />
                      )}
                      style={{ width: '100%' }}
                    />
                  </Grid>
                </Grid>
                <Grid size={1}>
                  <IconButton color="primary" onClick={() => removeItem(key)} title="Remove item">
                    <ClearIcon color="inherit" />
                  </IconButton>
                </Grid>
              </Grid>
            </Box>
          )
        })}
        <Grid container spacing={2}>
          <Grid>
            <IconButton color="primary" onClick={addNewItem} title="Add new item">
              <AddIcon color="inherit" />
            </IconButton>
          </Grid>
        </Grid>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            type="submit"
            color="primary"
            variant="outlined"
            onClick={() => submit(equipmentData)}
            disabled={loadingSubmit}
          >
            Save Equipment
          </Button>
        </Box>
      </Box>
    </>
  )
}

export default EditMedicalEquipment
